/**
 * SupabaseAuthService — first provider implementation of AuthService.
 *
 * Connects the existing provider-agnostic AuthService interface
 * to Supabase Auth. All auth operations go through the Supabase
 * client. Error translation uses the centralized error handler.
 *
 * Swapping providers means implementing AuthService with a
 * different client — no UI, hook, or context changes required.
 */

import type { User as SupabaseAuthUser } from '@supabase/supabase-js';
import { getSupabase } from '@/lib/supabase';
import { translateSupabaseAuthError, networkError, isTimeoutError } from '@/lib/errors';
import type { AuthService } from './AuthService';
import type { User, UserCredentials, UserRegistration, PasswordResetRequest } from '@/types/auth';
import type { Role, Permission } from '@/types/auth';
import type { AuthResult } from '@/types/auth/result';
import { SupabaseUserRepository } from '@/app/database/repositories/UserRepository';
import type { UserRow } from '@/types/database';

const userRepo = new SupabaseUserRepository();

const CREATOR_PERMISSIONS: Permission[] = ['application:submit', 'application:view', 'profile:edit'];
const ADMIN_PERMISSIONS: Permission[] = [...CREATOR_PERMISSIONS, 'admin:access', 'dashboard:view'];

function mapUser(authUser: SupabaseAuthUser, appUser: UserRow | null): User {
  const role: Role = appUser?.role === 'admin' ? 'admin' : 'creator';
  return {
    id: authUser.id,
    email: authUser.email ?? '',
    fullName: authUser.user_metadata?.full_name ?? authUser.email?.split('@')[0] ?? '',
    roles: [role],
    permissions: role === 'admin' ? ADMIN_PERMISSIONS : CREATOR_PERMISSIONS,
    emailVerified: appUser?.email_verified ?? (authUser.email_confirmed_at != null),
    createdAt: authUser.created_at ?? new Date().toISOString(),
  };
}

export const supabaseAuthService: AuthService = {
  async signIn(credentials: UserCredentials): Promise<AuthResult> {
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        return { success: false, error: translateSupabaseAuthError(error.message) };
      }

      if (!data.user) {
        return { success: false, error: { code: 'unknown_error', message: 'Sign in failed.' } };
      }

      const appUser = await userRepo.findById(data.user.id);
      return { success: true, user: mapUser(data.user, appUser) };
    } catch (err) {
      if (isTimeoutError(err)) {
        return { success: false, error: { code: 'network_error', message: 'The request timed out.' } };
      }
      return { success: false, error: networkError() };
    }
  },

  async register(data: UserRegistration): Promise<AuthResult> {
    try {
      const supabase = getSupabase();
      const { data: result, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            full_name: data.fullName,
            ...(data.referralCode ? { referral_code: data.referralCode } : {}),
          },
        },
      });

      if (error) {
        return { success: false, error: translateSupabaseAuthError(error.message) };
      }

      if (!result.user) {
        return { success: false, error: { code: 'unknown_error', message: 'Registration failed.' } };
      }

      const appUser = await userRepo.findById(result.user.id);
      return { success: true, user: mapUser(result.user, appUser) };
    } catch (err) {
      if (isTimeoutError(err)) {
        return { success: false, error: { code: 'network_error', message: 'The request timed out.' } };
      }
      return { success: false, error: networkError() };
    }
  },

  async signOut(): Promise<void> {
    try {
      const supabase = getSupabase();
      await supabase.auth.signOut();
    } catch {
      // sign-out failure is non-fatal
    }
  },

  async getCurrentUser(): Promise<User | null> {
    try {
      const supabase = getSupabase();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const appUser = await userRepo.findById(user.id);
      return mapUser(user, appUser);
    } catch {
      return null;
    }
  },

  async requestPasswordReset(request: PasswordResetRequest): Promise<AuthResult> {
    try {
      const supabase = getSupabase();
      const { error } = await supabase.auth.resetPasswordForEmail(request.email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        return { success: false, error: translateSupabaseAuthError(error.message) };
      }

      return { success: true };
    } catch (err) {
      if (isTimeoutError(err)) {
        return { success: false, error: { code: 'network_error', message: 'The request timed out.' } };
      }
      return { success: false, error: networkError() };
    }
  },

  async resetPassword(_token: string, newPassword: string): Promise<AuthResult> {
    try {
      const supabase = getSupabase();
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        return { success: false, error: translateSupabaseAuthError(error.message) };
      }

      return { success: true };
    } catch (err) {
      if (isTimeoutError(err)) {
        return { success: false, error: { code: 'network_error', message: 'The request timed out.' } };
      }
      return { success: false, error: networkError() };
    }
  },

  async verifyEmail(token: string): Promise<AuthResult> {
    try {
      const supabase = getSupabase();
      const { error } = await supabase.auth.verifyOtp({
        type: 'email',
        token_hash: token,
      });

      if (error) {
        return { success: false, error: translateSupabaseAuthError(error.message) };
      }

      return { success: true };
    } catch (err) {
      if (isTimeoutError(err)) {
        return { success: false, error: { code: 'network_error', message: 'The request timed out.' } };
      }
      return { success: false, error: networkError() };
    }
  },

  async refreshSession(): Promise<AuthResult> {
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase.auth.refreshSession();

      if (error) {
        return { success: false, error: translateSupabaseAuthError(error.message) };
      }

      if (!data.user) {
        return { success: false, error: { code: 'unknown_error', message: 'Session refresh failed.' } };
      }

      const appUser = await userRepo.findById(data.user.id);
      return { success: true, user: mapUser(data.user, appUser) };
    } catch (err) {
      if (isTimeoutError(err)) {
        return { success: false, error: { code: 'network_error', message: 'The request timed out.' } };
      }
      return { success: false, error: networkError() };
    }
  },
};
